import { ProfessorKaTile } from '../../components/ProfessorKaAvatar';
import { useResponsiveScale } from '../../hooks/useResponsiveRem';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import { subTestID } from '../../utils/testId';
import { SIGN_IN_KA_TILE_SIZE } from './SignInLayout.sizes';
import type { SignInHeaderProps } from './SignInLayout.types';

/**
 * The sign-in card's header — Ka's tile, the welcome title, and the one-line
 * promise under it. Centred, 12px apart (Figma 29991:39343).
 *
 * The tile is the DS `ProfessorKaTile` in its static form (no `onPress`, so no
 * button role and no floating shadow) at the card's own 56px, ramped with the
 * DS scale like every other tile. Ka names himself — the avatar carries
 * `PROFESSOR_KA_LABEL` — so the header adds no second label. A client that
 * can animate Ka injects him through `ka` (web: the floating Rive).
 */
export function SignInHeader({
  title,
  subtitle,
  ka,
  className,
  testID,
}: SignInHeaderProps) {
  const scale = useResponsiveScale();

  return (
    <View className={cn('w-full items-center gap-3', className)} testID={testID}>
      {ka ?? <ProfessorKaTile size={SIGN_IN_KA_TILE_SIZE * scale} />}
      <View className="w-full items-center gap-1">
        <Text
          as="h1"
          className="text-center text-h1 font-bold text-secondary-800 native:font-mona-bold">
          {title}
        </Text>
        {/* Absent, not empty: mobile's forgot-password request step has a
            title only, and an empty Text would still reserve its line. */}
        {subtitle ? (
          <Text
            as="p"
            testID={subTestID(testID, 'subtitle')}
            className="text-center text-h3 font-semibold text-secondary-400 native:font-mona-semibold">
            {subtitle}
          </Text>
        ) : null}
      </View>
    </View>
  );
}
(SignInHeader as { displayName?: string }).displayName = 'SignInHeader';
